import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getSEOConfig,
  isSelfManagedSEOPath,
  BASE_URL,
  DEFAULT_OG_IMAGE,
  RouteSEOConfig,
} from '@/config/seoConfig';
import { isNoIndexRoute } from '@/config/routeVisibility';
import { BUSINESS_IDENTITY } from '@/config/businessIdentity';

interface PageSEOProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  publishedDate?: string;
  modifiedDate?: string;
  author?: string;
  keywords?: string[];
  breadcrumbs?: Array<{ name: string; url: string }>;
}

export const PageSEO: React.FC<PageSEOProps> = ({
  title: propTitle,
  description: propDescription,
  canonicalPath,
  image = DEFAULT_OG_IMAGE,
  type: propType,
  noIndex = false,
  publishedDate,
  modifiedDate,
  author,
  keywords = [],
  breadcrumbs
}) => {
  const location = useLocation();
  const currentPath = canonicalPath || location.pathname;
  
  // Get SEO config from central config
  const seoConfig = useMemo(() => getSEOConfig(location.pathname), [location.pathname]);
  
  // Determine if page should be indexed
  const shouldIndex = useMemo(() => {
    if (noIndex) return false;
    if (isNoIndexRoute(location.pathname)) return false;
    if (seoConfig.noIndex) return false;
    return true;
  }, [location.pathname, noIndex, seoConfig.noIndex]);
  
  // Use props if provided, otherwise fall back to central config
  const seoTitle = propTitle || seoConfig.title;
  const seoDescription = propDescription || seoConfig.description;
  const seoType = propType || seoConfig.ogType;
  const canonicalUrl = new URL(currentPath, BASE_URL).toString();
  const fullImageUrl = image.startsWith('http') ? image : `${BASE_URL}${image}`;

  useEffect(() => {
    // Update document title
    document.title = seoTitle;
    
    // Helper to update/create meta tags
    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };

    const removeMeta = (name: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      document.querySelector(`meta[${attr}="${name}"]`)?.remove();
    };
    
    // Helper to update/create link tags
    const setLink = (rel: string, href: string, extra?: Record<string, string>) => {
      let link = document.querySelector(`link[rel="${rel}"]${extra?.hreflang ? `[hreflang="${extra.hreflang}"]` : ''}`);
      if (link) {
        link.setAttribute('href', href);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('href', href);
        if (extra) {
          Object.entries(extra).forEach(([key, value]) => {
            link!.setAttribute(key, value);
          });
        }
        document.head.appendChild(link);
      }
    };

    // Core SEO Meta Tags
    setMeta('description', seoDescription);
    if (keywords.length > 0) {
      setMeta('keywords', keywords.join(', '));
    } else {
      removeMeta('keywords');
    }
    
    // Robots meta
    const robotsContent = shouldIndex 
      ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      : 'noindex, nofollow';
    setMeta('robots', robotsContent);
    setMeta('googlebot', robotsContent);
    
    // Canonical URL
    setLink('canonical', canonicalUrl);
    
    // OpenGraph Tags
    setMeta('og:title', seoTitle, true);
    setMeta('og:description', seoDescription, true);
    setMeta('og:image', fullImageUrl, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:type', seoType, true);
    setMeta('og:site_name', 'Dialgia Group', true);
    setMeta('og:locale', 'en_GB', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:image:alt', seoTitle, true);
    
    // Twitter Cards
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', BUSINESS_IDENTITY.social.xHandle);
    setMeta('twitter:creator', BUSINESS_IDENTITY.social.xHandle);
    setMeta('twitter:title', seoTitle);
    setMeta('twitter:description', seoDescription.substring(0, 200));
    setMeta('twitter:image', fullImageUrl);
    setMeta('twitter:image:alt', seoTitle);
    
    // Article metadata must reflect real editorial dates, not render time.
    if (publishedDate) {
      setMeta('article:published_time', publishedDate, true);
    } else {
      removeMeta('article:published_time', true);
    }
    if (modifiedDate) {
      setMeta('article:modified_time', modifiedDate, true);
    } else {
      removeMeta('article:modified_time', true);
    }
    
    // Author
    if (author) {
      setMeta('author', author);
      setMeta('article:author', author, true);
    } else {
      removeMeta('author');
      removeMeta('article:author', true);
    }

    if (seoType !== 'product') {
      removeMeta('product:price:amount', true);
      removeMeta('product:price:currency', true);
      removeMeta('product:availability', true);
      removeMeta('product:condition', true);
    }
    
    // Hreflang for international SEO
    setLink('alternate', canonicalUrl, { hreflang: 'en-GB' });
    setLink('alternate', canonicalUrl, { hreflang: 'x-default' });

    // Add breadcrumb structured data if provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      const existingBreadcrumbScript = document.querySelector('script[data-seo="breadcrumb"]');
      if (existingBreadcrumbScript) {
        existingBreadcrumbScript.remove();
      }
      
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
        }))
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'breadcrumb');
      script.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      const breadcrumbScript = document.querySelector('script[data-seo="breadcrumb"]');
      if (breadcrumbScript) {
        breadcrumbScript.remove();
      }
    };
  }, [seoTitle, seoDescription, keywords, canonicalUrl, fullImageUrl, seoType, shouldIndex, publishedDate, modifiedDate, author, breadcrumbs]);

  return null;
};

// Hook for easy SEO management
export const usePageSEO = (options: PageSEOProps) => {
  const location = useLocation();
  
  useEffect(() => {
    // Re-run SEO setup on route change
  }, [location.pathname, options]);
  
  return <PageSEO {...options} />;
};

// Auto SEO component that uses central config automatically
export const AutoPageSEO: React.FC<{ breadcrumbs?: Array<{ name: string; url: string }> }> = ({ breadcrumbs }) => {
  const location = useLocation();
  if (isSelfManagedSEOPath(location.pathname)) return null;

  return <PageSEO breadcrumbs={breadcrumbs} />;
};
